import { create } from 'zustand'
import {
  Partner,
  PartnerWithContacts,
  PartnerPagination,
  PartnerSummary,
  CreatePartnerRequest,
  UpdatePartnerRequest,
  PartnerStatus
} from '../types/partner'
import { partnerService } from '../services/partnerService'

interface PartnerState {
  // State
  partners: Partner[]
  summary: PartnerSummary | null
  pagination: PartnerPagination | null
  loading: boolean
  error: string | null
  selectedPartner: PartnerWithContacts | null

  // Actions
  loadPartners: (
    customerId: string,
    search?: string,
    status?: PartnerStatus,
    categoryId?: string,
    tierId?: string,
    page?: number,
    limit?: number,
    startDate?: string,
    endDate?: string
  ) => Promise<void>
  loadPartner: (id: string) => Promise<void>
  createPartner: (customerId: string, data: CreatePartnerRequest) => Promise<Partner>
  updatePartner: (id: string, customerId: string, data: UpdatePartnerRequest) => Promise<Partner>
  deletePartner: (id: string) => Promise<void>
  setSelectedPartner: (partner: PartnerWithContacts | null) => void
  reset: () => void
}

const usePartnerStore = create<PartnerState>((set) => ({
  // Initial State
  partners: [],
  summary: null,
  pagination: null,
  loading: false,
  error: null,
  selectedPartner: null,

  // Actions
  loadPartners: async (
    customerId: string,
    search?: string,
    status?: PartnerStatus,
    categoryId?: string,
    tierId?: string,
    page?: number,
    limit?: number,
    startDate?: string,
    endDate?: string
  ) => {
    set({ loading: true, error: null })
    try {
      const { data, summary, pagination } = await partnerService.getPartners(
        customerId,
        search,
        status,
        categoryId,
        tierId,
        page,
        limit,
        startDate,
        endDate
      )
      set({ partners: data, summary, pagination, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load partners',
        loading: false
      })
    }
  },

  loadPartner: async (id: string) => {
    set({ loading: true, error: null })
    try {
      const partner = await partnerService.getPartner(id)
      set({ selectedPartner: partner, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load partner',
        loading: false
      })
    }
  },

  createPartner: async (customerId: string, data: CreatePartnerRequest) => {
    const partner = await partnerService.createPartner(customerId, data)
    // Note: We don't add to local state here because the API response may not include
    // the full partner_categories and partner_tiers objects needed for display.
    // The PartnersList component will reload partners after successful creation.
    return partner
  },

  updatePartner: async (id: string, customerId: string, data: UpdatePartnerRequest) => {
    const updated = await partnerService.updatePartner(id, customerId, data)
    // Note: We don't update local state here because the API response may not include
    // the full partner_categories and partner_tiers objects needed for display.
    // The EditPartnerDrawer component will reload partners after successful update.
    return updated
  },

  deletePartner: async (id: string) => {
    await partnerService.deletePartner(id)

    // Remove from local state
    set((state) => ({
      partners: state.partners.filter((partner) => partner.id !== id),
      selectedPartner: state.selectedPartner?.id === id ? null : state.selectedPartner
    }))
  },

  setSelectedPartner: (partner: PartnerWithContacts | null) => {
    set({ selectedPartner: partner })
  },

  reset: () =>
    set({
      partners: [],
      summary: null,
      pagination: null,
      loading: false,
      error: null,
      selectedPartner: null
    })
}))

export default usePartnerStore
